import { useState, useEffect, useContext } from "react";
import envConfig from '../envConfig';
import { AuthContext } from "../App";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import DashboardAdvisory from './DashboardAdvisory';
import DeliveryDashboard from './DeliveryDashboard';
import Footer from "./Footer";
import Swal from 'sweetalert2';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function Dashboard() {
	let userDetails = JSON.parse(localStorage.getItem("userDetails"));
	const [isLoading, setIsLoading] = useState(false);
	const { isAuthenticated, logout } = useContext(AuthContext);
	const isSuperAdmin = userDetails?.role_id === 1;
	const hasFixedCountry = userDetails?.allowed_country_id != null;

	// Set initial country: Super Admin defaults to country_id 2, others use their allowed_country_id
	const [selectedCountryId, setSelectedCountryId] = useState(
		isSuperAdmin ? 2 : (userDetails?.allowed_country_id ?? null)
	);

	const [countryOptions, setCountryOptions] = useState([]);
	const [speciesOptions, setSpeciesOptions] = useState([]);
	const [selectedSpeciesId, setSelectedSpeciesId] = useState(
		isSuperAdmin ? 3 : (userDetails.allowed_country_id === 1 ? 1 : 3)
	);
	const [selectedSpecies, setSelectedSpecies] = useState("");
	const [totalRecipients, setTotalRecipients] = useState([]);
	const [fromDate, setFromDate] = useState(new Date().toISOString().split("T")[0]);
	const [provinceOptions, setProvinceOptions] = useState([]);
	const [selectedProvinceId, setSelectedProvinceId] = useState(null);
	const [allDistricts, setAllDistricts] = useState([]);
	const [districtOptions, setDistrictOptions] = useState([]);
	const [selectedDistrictId, setSelectedDistrictId] = useState(null);
	const [riskAnalysis, setRiskAnalysis] = useState(null);
	const [temperatureData, setTemperatureData] = useState([]);
	const [selectedCamp, setSelectedCamp] = useState(null);
	const [activeAdvisory, setActiveAdvisory] = useState(null);
	const [campBeneficiaryCounts, setCampBeneficiaryCounts] = useState([0, 0]);
	const [userRoles, setUserRoles] = useState([]);
	const [farmersBySpecies, setFarmersBySpecies] = useState([]);
	const [riskDataAbsentText, setRiskDataAbsentText] = useState("Please choose a district and hit submit to see risk analysis")
	const [weeklyAdvisory, setWeeklyAdvisory] = useState(null);
	const [activeWeeklyAdvisory, setActiveWeeklyAdvisory] = useState(null);
	const [activeMainTab, setActiveMainTab] = useState('advisory');

	const speciesImgPath = {
		1: "1-0",
		2: "1-1",
		3: "2-1",
		4: "2-0",
	};

	const handleApiError = (error, response) => {
		if (
			response &&
			response.status === 401 &&
			error.message.includes('Your authentication token is invalid')
		) {
			setTimeout(() => {
				logout();
				window.location.href = '/login';
			}, 0);
			return 'Session expired. Please log in again.';
		}
		return error?.message || 'An error occurred. Please try again.';
	};

	const optionsTotalRecipients = totalRecipients.length ? {
		"chart": { "type": "column", "height": 300 },
		"credits": { "enabled": false },
		"title": { "text": null },
		"subtitle": { "text": null },
		"xAxis": {
			"categories": totalRecipients.map(a => a.adm3),
			"crosshair": true,
			"accessibility": { "description": "Camps" },
		},
		"yAxis": {
			"min": 0,
			"title": { "text": null },
		},
		"tooltip": { "valueSuffix": null, "shared": true },
		"plotOptions": {
			"column": { "pointPadding": 0.2, "borderWidth": 0 },
		},
		"series": [
			{
				"name": "Total Extension Officers",
				"color": '#E1A325',
				"data": totalRecipients.map(a => a.extoff_count)
			},
			{
				"name": "Total Fish Farmers",
				"color": '#1A4985',
				"data": totalRecipients.map(a => a.farmer_count)
			},
		]
	} : null;

	const optionsRiskData = riskAnalysis && riskAnalysis?.chart_data.length
		? {
			"chart": { "type": "column", "height": 400 },
			"credits": { "enabled": false },
			"title": { "text": null },
			"subtitle": { "text": null },
			"xAxis": {
				"categories": riskAnalysis.chart_data.map(a => a.adm3_name),
				"crosshair": true,
				"accessibility": { "description": "Camps" },
			},
			"yAxis": {
				"min": 0,
				"title": { "text": null },
			},
			"tooltip": { "valueSuffix": null, "shared": true },
			"plotOptions": {
				"column": { "pointPadding": 0.2, "borderWidth": 0 },
				"series": {
					"cursor": "pointer",
					"point": {
						"events": {
							"click": function () {
								const clickedAdm3 = riskAnalysis.chart_data[this.index];
								getCampTemp(clickedAdm3);
								const match = totalRecipients.find(a => a.adm3_id === clickedAdm3.adm3_id);
								setCampBeneficiaryCounts(match ? [match.farmer_count, match.extoff_count] : [0, 0]);
							}
						}
					}
				}
			},
			"series": [
				{
					"name": "Emergency instances",
					"color": '#FF0000',
					"data": riskAnalysis.chart_data.map(a => a?.["EMERGENCY"] ?? 0),
				},
				{
					"name": "High risk instances",
					"color": '#E1A325',
					"data": riskAnalysis.chart_data.map(a => a?.["HIGH RISK"] ?? 0),
				},
				{
					"name": "Intermediate risk instances",
					"color": '#D6CC6F',
					"data": riskAnalysis.chart_data.map(a => a?.["INTERMEDIATE RISK"] ?? 0),
				},
				{
					"name": "Normal Instances",
					"color": '#43A047',
					"data": riskAnalysis.chart_data.map(a => a?.["NORMAL"] ?? 0),
				},
			]
		} : null;

	const optionsTemperature = temperatureData.length
		? {
			"chart": { "type": "line", "height": 400 },
			"credits": { "enabled": false },
			"title": { "text": null },
			"subtitle": { "text": null },
			"xAxis": {
				"categories": temperatureData.map(a => a.ref_date),
				"crosshair": true,
				"accessibility": { "description": "Date" },
			},
			"yAxis": {
				"min": 0,
				"title": { "text": "Temperature (°C)" },
			},
			"tooltip": { "valueSuffix": null, "shared": true },
			"plotOptions": {
				"line": { "pointPadding": 0.2, "borderWidth": 0 },
			},
			"series": [
				{
					"name": "Max Air Temp",
					"color": '#344BFD',
					"data": temperatureData.map(a => a.temp_max),
				},
				{
					"name": "Min Air Temp",
					"color": '#58AB47',
					"data": temperatureData.map(a => a.temp_min),
				},
				{
					"name": "Pond Temp",
					"color": '#FFC130',
					"data": temperatureData.map(a => a.predicted_pond_temperature),
				},
			]
		}
		: null;

	const riskLevels = ['NORMAL', 'INTERMEDIATE RISK', 'HIGH RISK', 'EMERGENCY'];

	const optionsFarmerRisk = riskAnalysis?.farmer_counts ? {
		"chart": { "type": "column", "height": 300 },
		"credits": { "enabled": false },
		"title": { "text": null },
		"subtitle": { "text": null },
		"xAxis": {
			"categories": riskLevels,
			"crosshair": true,
			"accessibility": { "description": "Risk Levels" },
		},
		"yAxis": {
			"min": 0,
			"title": { "text": null },
		},
		"tooltip": { "valueSuffix": null, "shared": true },
		"plotOptions": {
			"column": { "pointPadding": 0.2, "borderWidth": 0 },
		},
		"series": [
			{
				"name": "Farmers",
				"data": riskLevels.map(key => riskAnalysis.farmer_counts[key] || 0),
				"colorByPoint": true,
				"colors": ['#43A047', '#D6CC6F', '#E1A325', '#FF0000']
			}
		]
	} : null;

	const optionsCampRisk = riskAnalysis?.camp_counts ? {
		"chart": { "type": "column", "height": 300 },
		"credits": { "enabled": false },
		"title": { "text": null },
		"subtitle": { "text": null },
		"xAxis": {
			"categories": riskLevels,
			"crosshair": true,
			"accessibility": { "description": "Risk Levels" },
		},
		"yAxis": {
			"min": 0,
			"title": { "text": null },
		},
		"tooltip": { "valueSuffix": null, "shared": true },
		"plotOptions": {
			"column": { "pointPadding": 0.2, "borderWidth": 0 },
		},
		"series": [
			{
				"name": "Camps",
				"data": riskLevels.map(key => riskAnalysis.camp_counts[key] || 0),
				"colorByPoint": true,
				"colors": ['#43A047', '#D6CC6F', '#E1A325', '#FF0000']
			}
		]
	} : null;

	const optionsUserRoles = userRoles.length ? {
		"chart": { "type": "column", "height": 300 },
		"credits": { "enabled": false },
		"title": { "text": null },
		"subtitle": { "text": null },
		"xAxis": {
			"categories": userRoles.map(a => a.user_role),
			"crosshair": true,
			"accessibility": { "description": "User Roles" },
		},
		"yAxis": {
			"min": 0,
			"title": { "text": null },
		},
		"tooltip": { "valueSuffix": null, "shared": true },
		"plotOptions": {
			"column": { "pointPadding": 0.2, "borderWidth": 0 },
		},
		"series": [
			{
				"name": "Count",
				"data": userRoles.map(a => a.count)
			}
		]
	} : null;

	const optionsFarmersSpecies = farmersBySpecies.length ? {
		"chart": { "type": "column", "height": 300 },
		"credits": { "enabled": false },
		"title": { "text": null },
		"subtitle": { "text": null },
		"xAxis": {
			"categories": farmersBySpecies.map(a => a.fish_species),
			"crosshair": true,
			"accessibility": { "description": "Fish Species" },
		},
		"yAxis": {
			"min": 0,
			"title": { "text": null },
		},
		"tooltip": { "valueSuffix": null, "shared": true },
		"plotOptions": {
			"column": { "pointPadding": 0.2, "borderWidth": 0 },
		},
		"series": [
			{
				"name": "Farmers",
				"data": farmersBySpecies.map(a => a.farmer_count)
			}
		]
	} : null;

	const getAuthHeaders = () => {
		const token = localStorage.getItem('profishBlue_authToken');
		const expiryTime = localStorage.getItem('profishBlue_tokenExpiry');
		const isTokenExpired = !token || !expiryTime || Date.now() > parseInt(expiryTime, 10);
		if (isTokenExpired) {
			logout();
			window.location.href = '/login';
			return {};
		}
		return {
			'Content-Type': 'application/json',
			'Authorization': token,
		}
	}

	const fetchInitialData = () => {
		setIsLoading(true);
		let reqRiskBody = {
			"country_id": selectedCountryId,
			"species_id": selectedSpeciesId,
			"ref_date": fromDate,
		};
		Promise.all([
			fetch(
				`${envConfig.API_URL}/lookups/locations/country`,
				{ 'method': "GET", 'headers': getAuthHeaders() }
			),
			fetch(
				`${envConfig.API_URL}/lookups/locations/adm1?country_id=${selectedCountryId}`,
				{ 'method': "GET", 'headers': getAuthHeaders() },
			),
			fetch(
				`${envConfig.API_URL}/lookups/locations/adm2?country_id=${selectedCountryId}`,
				{ 'method': "GET", 'headers': getAuthHeaders() },
			),
			fetch(
				`${envConfig.API_URL}/lookups/fishes/species?country_id=${selectedCountryId}`,
				{ 'method': "GET", 'headers': getAuthHeaders() },
			),
			fetch(
				`${envConfig.API_URL}/stats/total_recipients?country_id=${selectedCountryId}`,
				{ 'method': "GET", 'headers': getAuthHeaders() },
			),

			fetch(
				`${envConfig.API_URL}/stats/user_roles?country_id=${selectedCountryId}`,
				{ 'method': "GET", 'headers': getAuthHeaders() },
			),
			fetch(
				`${envConfig.API_URL}/stats/farmers/species?country_id=${selectedCountryId}`,
				{ 'method': "GET", 'headers': getAuthHeaders() },
			)
		])
			.then(async responses => {
				const jsons = await Promise.all(
					responses.map(async (res) => {
						const data = await res.json().catch(() => ({}));
						if (!res.ok) {
							const error = new Error(data.message || `HTTP error! status: ${res.status}`);
							throw { error, response: res };
						}
						return data;
					})
				);
				return jsons;
			})
			.then(([
					lkpCountry, lkpProvince, lkpDistrict, lkpSpecies, 
					respTotalRecepients, respUserRoles, respFarmersSpecies
				]) => {
				setCountryOptions(lkpCountry.data);
				setProvinceOptions(lkpProvince.data);
				setAllDistricts(lkpDistrict.data);
				// initial districts for first province
				let initialProvinceId = lkpDistrict.data[0].adm1_id;
				setSelectedProvinceId(initialProvinceId);
				let initialFilteredDistricts = lkpDistrict.data.filter(a => a.adm1_id === initialProvinceId);
				if(initialFilteredDistricts.length){
					setDistrictOptions(initialFilteredDistricts);
					setSelectedDistrictId(initialFilteredDistricts[0].adm2_id)
				} else{
					setDistrictOptions([]);
					setSelectedDistrictId(null);
				}
				setSpeciesOptions(lkpSpecies.data);
				setTotalRecipients(respTotalRecepients.data);
				setUserRoles(respUserRoles.data);
				setFarmersBySpecies(respFarmersSpecies.data);
			})
			.catch(({ error, response }) => {
				const errorMessage = handleApiError(error, response);
				console.error("Error fetching initial data:", errorMessage);
				Swal.fire({
					title: "Error!",
					text: errorMessage || "Error getting the initial data for this dashboard page",
					icon: "error",
					confirmButtonText: "OK",
				});
			})
			.finally(() => setIsLoading(false));
	}

	useEffect(() => {
		if (selectedCountryId) {
			fetchInitialData();
		}
	}, [selectedCountryId]);

	const onCountryChange = countryId => {
		setIsLoading(true);
		setCampBeneficiaryCounts([0, 0]);
		setSelectedCountryId(countryId);
		setActiveAdvisory(null);
		setSelectedCamp("");
		Promise.all([
			fetch(
				`${envConfig.API_URL}/lookups/fishes/species?country_id=${countryId}`,
				{ 'method': "GET", 'headers': getAuthHeaders() },
			),
			fetch(
				`${envConfig.API_URL}/stats/total_recipients?country_id=${countryId}`,
				{ 'method': "GET", 'headers': getAuthHeaders() },
			),
			fetch(
				`${envConfig.API_URL}/stats/farmers/species?country_id=${countryId}`,
				{ 'method': "GET", 'headers': getAuthHeaders() },
			)
		])
			.then(async responses => {
				const jsons = await Promise.all(
					responses.map(async (res) => {
						const data = await res.json().catch(() => ({}));
						if (!res.ok) {
							const error = new Error(data.message || `HTTP error! status: ${res.status}`);
							throw { error, response: res };
						}
						return data;
					})
				);
				return jsons;
			})
			.then(([lkpSpecies, respTotalRecepients, respFarmersSpecies]) => {
				setSpeciesOptions(lkpSpecies.data);
				setTotalRecipients(respTotalRecepients.data);
				setFarmersBySpecies(respFarmersSpecies.data);
				const firstSpeciesId = lkpSpecies.data?.[0]?.species_id;
				setSelectedSpeciesId(firstSpeciesId);
				onSubmitForRiskData(countryId, firstSpeciesId, fromDate, selectedProvinceId, selectedDistrictId);
			})
			.catch(({ error, response }) => {
				const errorMessage = handleApiError(error, response);
				console.error("Error fetching country-specific data:", errorMessage);
				Swal.fire({
					title: "Error!",
					text: errorMessage || "Error getting the country-specific datasets",
					icon: "error",
					confirmButtonText: "OK",
				});
			})
			.finally(() => setIsLoading(false));
	}

	const onProvinceChange = provinceId => {
		setSelectedProvinceId(provinceId);
		let filteredDistricts = allDistricts.filter(b => b.adm1_id === provinceId).map(b => {
			return {"adm2_id": b.adm2_id, "adm2": b.adm2};
		});
		if(filteredDistricts.length){
			setDistrictOptions(filteredDistricts);
			setSelectedDistrictId(filteredDistricts[0].adm2_id)
		} else{
			setDistrictOptions([]);
			setSelectedDistrictId(null);
		}
	}

	const onSubmitForRiskData = (
		countryId = selectedCountryId, speciesId = selectedSpeciesId, refDate = fromDate,
		provinceId = selectedProvinceId, districtId = selectedDistrictId,
	) => {
		setIsLoading(true);
		setCampBeneficiaryCounts([0, 0]);
		setActiveAdvisory(null);
		setSelectedCamp("");
		let reqBody = {
			"country_id": countryId,
			"species_id": speciesId,
			"ref_date": refDate,
			"adm1_id": provinceId,
			"adm2_id": districtId,
		};
		fetch(
			`${envConfig.API_URL}/stats/risk_analysis`,
			{ "method": "POST", "headers": getAuthHeaders(), "body": JSON.stringify(reqBody) },
		)
			.then(async (response) => {
				const data = await response.json().catch(() => ({}));
				if (!response.ok) {
					const error = new Error(data.message || `HTTP error! status: ${response.status}`);
					throw { error, response };
				}
				return data;
			})
			.then(respRiskAnalysis => {
				setRiskAnalysis(respRiskAnalysis.data);
			})
			.catch(({ error, response }) => {
				setRiskAnalysis(null);
				setRiskDataAbsentText("Risk analysis data unavailable for the selection");
				Swal.fire({
					title: "Error!",
					text: "Risk analysis data unavailable for the selection",
					icon: "error",
					confirmButtonText: "OK",
				});
				const errorMessage = handleApiError(error, response);
				console.error("Error fetching data:", errorMessage);
				Swal.fire({
					title: "Error!",
					text: errorMessage || "Unable to fetch the risk analysis data",
					icon: "error",
					confirmButtonText: "OK",
				});
			})
			.finally(() => setIsLoading(false));
	}

	const getCampTemp = camp => {
		setIsLoading(true);
		setActiveAdvisory(null);
		setActiveWeeklyAdvisory(null);
		setWeeklyAdvisory(null);
		let reqCampBody = {
			"country_id": selectedCountryId,
			"species_id": selectedSpeciesId,
			"adm3_id": camp.adm3_id,
			"ref_date": fromDate,
		};
		fetch(
			`${envConfig.API_URL}/stats/camp`,
			{ "method": "POST", "headers": getAuthHeaders(), "body": JSON.stringify(reqCampBody) },
		)
			.then(async (response) => {
				const data = await response.json().catch(() => ({}));
				if (!response.ok) {
					const error = new Error(data.message || `HTTP error! status: ${response.status}`);
					throw { error, response };
				}
				return data;
			})
			.then(respCampData => {
				setTemperatureData(respCampData.data);
				setSelectedCamp(`${camp.adm3_name}, ${camp.adm2_name}, ${camp.adm1_name}`);
				setSelectedSpecies(speciesOptions.find(a => a.species_id == selectedSpeciesId).species);
				setTimeout(() => { window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }); }, 150);
			})
			.catch(({ error, response }) => {
				setTemperatureData([]);
				setSelectedSpecies("")
				setSelectedCamp("");
				const errorMessage = handleApiError(error, response);
				console.error("Error fetching data:", errorMessage);
				Swal.fire({
					title: "Error!",
					text: errorMessage || "Unable to fetch the camp advisory data",
					icon: "error",
					confirmButtonText: "OK",
				});
			})
			.finally(() => setIsLoading(false));

		// Fetch weekly advisory in parallel
		fetch(
			`${envConfig.API_URL}/advisory/weekly_advisory?country_id=${selectedCountryId}&adm3_id=${camp.adm3_id}`,
			{ "method": "GET", "headers": getAuthHeaders() },
		)
			.then(r => r.json())
			.then(result => { if (result.success === 1) setWeeklyAdvisory(result); })
			.catch(() => {});
	}

	const exportPDF = async (name) => {
		setIsLoading(true);
		const pdf = new jsPDF('p', 'mm', 'a4');
		const margin = 10;
		let yOffset = margin;
		const title = 'Dashboard';
		const today = new Date();
		const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
		const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		const date = `${days[today.getDay()]}, ${today.getDate()} ${months[today.getMonth()]}`;
		const pageWidth = 210;
		const usableWidth = pageWidth - 2 * margin;
		pdf.setFontSize(16);
		const titleWidth = pdf.getTextWidth(title);
		const titleX = margin + (usableWidth - titleWidth) / 2;
		pdf.text(title, titleX, yOffset + 10);
		pdf.setFontSize(12);
		const dateWidth = pdf.getTextWidth(date);
		const dateX = pageWidth - margin - dateWidth;
		pdf.text(date, dateX, yOffset + 10);
		yOffset += 20 + 10;
		const elements = document.querySelectorAll('.exportable-section');
		document.querySelectorAll('.no-export').forEach(el => el.style.display = 'none');
		try {
			for (let i = 0; i < elements.length; i++) {
				const element = elements[i];
				let imgData;
				let elementHeight = element.offsetHeight;
				let elementWidth = element.offsetWidth;
				const canvas = await html2canvas(element, { scale: 1, backgroundColor: '#fff' });
				imgData = canvas.toDataURL('image/png');
				elementHeight = canvas.height;
				elementWidth = canvas.width;
				if (!imgData) continue;
				const imgWidth = 190;
				const pageHeight = 297;
				const imgHeight = (elementHeight * imgWidth) / elementWidth;
				if (yOffset + imgHeight > pageHeight - margin) {
					pdf.addPage();
					yOffset = margin;
				}
				pdf.addImage(imgData, 'PNG', margin, yOffset, imgWidth, imgHeight);
				yOffset += imgHeight + 10;
			}
		} finally {
			document.querySelectorAll('.no-export').forEach(el => el.style.display = '');
		}
		pdf.save(`${name}.pdf`);
		setIsLoading(false);
	};

	return (<>
		<div className="container-fluid mt-60px">
			<div className="row pt-3 px-3">
				<div className="col-sm-12 col-md-12 col-lg-12">
					<div className="threshold-box">
						<div className="d-flex justify-content-between algn-items-center">
							<div> <h5>Dashboard</h5></div>
							<div className="d-flex align-items-center">
								{activeMainTab === 'delivery' ? (
									<>
										<label style={{ whiteSpace: 'nowrap' }}>Select Country</label>
										<select className="form-select h40px ms-2">
											<option value="2">Zambia</option>
											<option value="1" disabled>Malawi</option>
										</select>
									</>
								) : (isSuperAdmin || hasFixedCountry) ? (
									<>
										<label style={{ whiteSpace: 'nowrap' }}>Select Country</label>
										<select
											className="form-select h40px ms-2"
											value={selectedCountryId || ''}
											onChange={e => onCountryChange(+e.target.value)}
											disabled={hasFixedCountry}
										>
											{countryOptions.map(a => (
												<option value={a.country_id} key={a.country_id}>
													{a.country}
												</option>
											))}
										</select>
									</>
								) : null}
								{activeMainTab !== 'delivery' && (
									<button className="btn btn-primary ms-2" onClick={() => exportPDF("Dashboard_Report")}>
										Export&nbsp;to&nbsp;PDF
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
				{/* ---- Main tab bar ---- */}
				<div className="col-sm-12 col-md-12 col-lg-12">
					<div className="d-flex mt-2" style={{ borderBottom: '1px solid #dee2e6' }}>
						{[
							{ key: 'advisory',   label: 'Advisory' },
							{ key: 'delivery',   label: 'Delivery' },
							{ key: 'recipients', label: 'Recipients' },
						].map(t => (
							<button
								key={t.key}
								className="btn btn-link text-decoration-none px-3 py-2"
								style={{
									borderBottom: activeMainTab === t.key ? '2px solid #1A4985' : '2px solid transparent',
									color: activeMainTab === t.key ? '#1A4985' : '#6c757d',
									borderRadius: 0,
									fontWeight: activeMainTab === t.key ? 600 : 400,
								}}
								onClick={() => setActiveMainTab(t.key)}
							>
								{t.label}
							</button>
						))}
					</div>
				</div>

				{/* ---- Delivery tab ---- */}
				{activeMainTab === 'delivery' && (
					<div className="col-sm-12 col-md-12 col-lg-12 mt-3">
						<DeliveryDashboard getAuthHeaders={getAuthHeaders} handleApiError={handleApiError} />
					</div>
				)}

				{/* ---- Recipients tab ---- */}
				{activeMainTab === 'recipients' && <>
				<div className="col-sm-12 col-md-12 col-lg-12">
					<div className="card mt-3 exportable-section">
						<div className="card-body">
							{
								totalRecipients.length > 0 && <>
									<h4 className="chartTitle mb-4">Recipient type</h4>
									<HighchartsReact highcharts={Highcharts} options={optionsTotalRecipients} />
								</>
							}
						</div>
					</div>
				</div>
				<div className="col-sm-12 col-md-12 col-lg-12">
					<div className="card mt-3 exportable-section">
						<div className="card-body">
							{
								userRoles.length > 0 && <>
									<h4 className="chartTitle mb-4">Number of Admins by User Roles</h4>
									<HighchartsReact highcharts={Highcharts} options={optionsUserRoles} />
								</>
							}
						</div>
					</div>
				</div>
				<div className="col-sm-12 col-md-12 col-lg-12">
					<div className="card mt-3 exportable-section">
						<div className="card-body">
							{
								farmersBySpecies.length > 0 && <>
									<h4 className="chartTitle mb-4">Number of Farmers by Fish Species</h4>
									<HighchartsReact highcharts={Highcharts} options={optionsFarmersSpecies} />
								</>
							}
						</div>
					</div>
				</div>
				</>}

				{/* ---- Advisory tab ---- */}
				{activeMainTab === 'advisory' && <>
				<div className="col-sm-12 col-md-12 col-lg-12">
					<div className="threshold-box my-3">
						<h5>ADVISORY</h5>
					</div>
					<div className="d-flex justify-content-start align-items-center">
						<div className="me-4">
							<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Species</label>
							<select className="form-select h40px ms-0"
								value={selectedSpeciesId}
								onChange={e => setSelectedSpeciesId(+e.target.value)}>
								{speciesOptions.map(a => (
									<option value={a.species_id} key={a.species_id}>
										{a.scientific_name}
									</option>
								))}
							</select>
						</div>
						<div className="me-4">
							<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Date</label>
							<input type="date" className="form-control"
								value={fromDate}
								onChange={e => setFromDate(e.target.value)}
							/>
						</div>
						<div className="me-4">
							<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Province</label>
							<select className="form-select h40px ms-0"
								value={selectedProvinceId}
								onChange={e => onProvinceChange(+e.target.value)}>
								{provinceOptions.map(a => 
									<option value={a.adm1_id} key={a.adm1_id}>
										{a.adm1}
									</option>
								)}
							</select>
						</div>
						<div className="me-4">
							<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>District</label>
							<select className="form-select h40px ms-0"
								value={selectedDistrictId}
								onChange={e => setSelectedDistrictId(+e.target.value)}>
								{districtOptions.map(a => 
									<option value={a.adm2_id} key={a.adm2_id}>
										{a.adm2}
									</option>
								)}
							</select>
						</div>
						<div className="ms-3 mt-3 pt-1">
							<button className="btn btn-applyNew text-center"
								style={{ width: 'auto' }}
								onClick={() => onSubmitForRiskData()}>
								Submit
							</button>
						</div>
					</div>
				</div>
				{riskAnalysis?.chart_data && Array.isArray(riskAnalysis.chart_data) && riskAnalysis.chart_data.length > 0 ? <>
					<div className="col-sm-12 col-md-12 col-lg-12">
						<div className="row mb-3 exportable-section">
							<div className="col-sm-12 col-md-2 col-lg-2">
								<div className="card mt-3">
									<div className="card-body">
										<div className="detailsBox p-2">
											<h3 className="mb-4 pb-6">{riskAnalysis?.risk_counts?.["EMERGENCY"] ?? 0}</h3>
											<p style={{ color: "red", fontSize: "20px", fontWeight: "bold" }}>
												Emergency
											</p>

										</div>
									</div>
								</div>
							</div>
							<div className="col-sm-12 col-md-2 col-lg-2">
								<div className="card mt-3">
									<div className="card-body">
										<div className="detailsBox p-2">
											<h3 className="mb-4 pb-6">{riskAnalysis?.risk_counts?.["HIGH RISK"] ?? 0}</h3>
											<p style={{ color: "#FF5722", fontSize: "20px", fontWeight: "bold" }}>
												High Risk
											</p>
										</div>
									</div>
								</div>
							</div>
							<div className="col-sm-12 col-md-2 col-lg-2">
								<div className="card mt-3">
									<div className="card-body">
										<div className="detailsBox p-2">
											<h3 className="mb-4 pb-6">{riskAnalysis?.risk_counts?.["INTERMEDIATE RISK"] ?? 0}</h3>
											<p style={{ color: "#D6CC6F", fontSize: "20px", fontWeight: "bold" }}>
												Intermediate Risk
											</p>
										</div>
									</div>
								</div>
							</div>
							<div className="col-sm-12 col-md-2 col-lg-2">
								<div className="card mt-3">
									<div className="card-body">
										<div className="detailsBox p-2">
											<h3 className="mb-4 pb-6">{riskAnalysis?.risk_counts?.["NORMAL"] ?? 0}</h3>
											<p style={{ color: "green", fontSize: "20px", fontWeight: "bold" }}>
												Normal
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="col-sm-12 col-md-12 col-lg-12">
						<div className="card mt-3 exportable-section">
							<div className="card-body">
								{
									riskAnalysis?.farmer_counts && <>
										<h4 className="chartTitle mb-4">Number of Farmers by Risk Level</h4>
										<HighchartsReact highcharts={Highcharts} options={optionsFarmerRisk} />
									</>
								}
							</div>
						</div>
					</div>
					<div className="col-sm-12 col-md-12 col-lg-12">
						<div className="card mt-3 exportable-section">
							<div className="card-body">
								{
									riskAnalysis?.camp_counts && <>
										<h4 className="chartTitle mb-4">Number of Camps by Risk Level</h4>
										<HighchartsReact highcharts={Highcharts} options={optionsCampRisk} />
									</>
								}
							</div>
						</div>
					</div>
					<div className="col-sm-12 col-md-12 col-lg-12">
						<div className="card mt-2 mb-2 exportable-section">
							<div className="card-body">
								<h4 className="chartTitle mb-4">Risk Analysis</h4>
								<HighchartsReact highcharts={Highcharts} options={optionsRiskData} key={riskAnalysis.chart_data.length} />
							</div>
						</div>
					</div>
				</> : <div className="text-center my-3">{riskDataAbsentText}</div>}

				{selectedCamp && temperatureData.length > 0 && <>
					<div className="threshold-box my-3 exportable-section">
						<h5 className="chartTitle mb-4">{selectedCamp}</h5>

					</div>
					<div className="col-sm-12 col-md-12 col-lg-12">
						<div className="card mt-2 mb-2 exportable-section">
							<div className="card-body">
								<h5 className="chartTitle mb-4">Air and Pond Temperatures </h5>
								<HighchartsReact highcharts={Highcharts} options={optionsTemperature} />
							</div>
						</div>
					</div>
				</>}
				{selectedCamp && <>
					<div className="col-sm-12 col-md-12 col-lg-12 exportable-section">
						<div className="table-responsive mt-3">
							<table className="table tbl_new tdColor table-bordered">
								<thead>
									<tr>
										<th><strong>Camp</strong></th>
										<th colSpan={temperatureData.length}>{selectedCamp}</th>
									</tr>

									<tr>
										<th><strong>Species</strong></th>
										<th colSpan={temperatureData.length}>{selectedSpecies}</th>
									</tr>
									<tr>
										<th><strong>Farmers registered</strong></th>
										<th colSpan={temperatureData.length}>{campBeneficiaryCounts[0]}</th>
									</tr>
									<tr>
										<th><strong>Extension officers registered</strong></th>
										<th colSpan={temperatureData.length}>{campBeneficiaryCounts[1]}</th>
									</tr>
									<tr className="no-export">
										<th><strong>{(() => {
											const fmtD = d => { const dt = new Date(d); return `${dt.toLocaleString('default', { month: 'short' })} ${dt.getDate()}`; };
											return weeklyAdvisory?.week_start
												? `Weekly Advisory for ${fmtD(weeklyAdvisory.week_start)} – ${fmtD(weeklyAdvisory.week_end)}`
												: 'Weekly Advisory';
										})()}</strong></th>
										{weeklyAdvisory?.data?.length > 0 ? (
											<th colSpan={temperatureData.length}>
												<span style={{ fontSize: '12px', whiteSpace: 'pre-line' }}>
													{weeklyAdvisory.data.find(d => d.language === 'english')?.advisory_text?.substring(0, 120)}...
												</span>
												<button
													className="btn btn-sm btn-link text-primary p-0 ms-2"
													onClick={() => {
														const eng = weeklyAdvisory.data.find(d => d.language === 'english');
														const local1 = weeklyAdvisory.data.find(d => d.language !== 'english' && d.language !== 'chichewa_zmb');
														const local2 = weeklyAdvisory.data.find(d => d.language === 'chichewa_zmb');
														setActiveWeeklyAdvisory({
															adm3_id: temperatureData[0]?.adm3_id,
															adm2_id: temperatureData[0]?.adm2_id,
															adm1_id: temperatureData[0]?.adm1_id,
															advisory_text: [eng?.advisory_text || ''],
															local_text: [local1?.advisory_text || ''],
															local_text2: local2 ? [local2.advisory_text] : null,
															local_language: selectedCountryId === 1 ? 'Chichewa' : 'Bemba',
															risk_level: weeklyAdvisory.data[0]?.risk_level,
															week_start: weeklyAdvisory.week_start,
															week_end: weeklyAdvisory.week_end,
															week_pond_temp_min: weeklyAdvisory.week_pond_temp_min,
															week_pond_temp_max: weeklyAdvisory.week_pond_temp_max,
														});
														setActiveAdvisory(null);
														setTimeout(() => { window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' }); }, 150);
													}}>
													Click to View
												</button>
											</th>
										) : (
											<th colSpan={temperatureData.length} style={{ color: '#6c757d', fontSize: '13px' }}>
												No weekly advisory available
											</th>
										)}
									</tr>
									<tr>
										<th><strong>Dates</strong></th>
										{temperatureData.map((a, i) => <th key={i}>{a.ref_date}</th>)}
									</tr>
								</thead>
								<tbody>
									<tr>
										<th><strong>Max Air Temp</strong></th>
										{temperatureData.map((a, i) => <td key={i}>{a.temp_max}</td>)}
									</tr>
									<tr>
										<th><strong>Min Air Temp</strong></th>
										{temperatureData.map((a, i) => <td key={i}>{a.temp_min}</td>)}
									</tr>
									<tr>
										<th><strong>Pond Temp</strong></th>
										{temperatureData.map((a, i) => <td key={i}>{a.predicted_pond_temperature}</td>)}
									</tr>
									<tr>
										<th><strong>Risk Level</strong></th>
										{temperatureData.map((a, i) => <td key={i}>{a.risk_level}</td>)}
									</tr>
									<tr>
										<th><strong>Advisory</strong></th>
										{temperatureData.map((a, i) => <td key={i} style={{ whiteSpace: 'pre-line' }}>{a.advisory_text.join("\n")}</td>)}
									</tr>
									<tr className="no-export">
										<th><strong>Action</strong></th>
										{temperatureData.map((a, i) => (
											<td key={i}>
												<button
													className="btn btn-sm btn-link text-primary p-0"
													onClick={() => {
														setActiveAdvisory(a);
														setTimeout(() => {
															window.scrollTo({
																top: document.documentElement.scrollHeight,
																behavior: "smooth",
															});
														}, 150);
													}}>
													Click to View
												</button>
											</td>
										))}
									</tr>
								</tbody>
							</table>
							{activeAdvisory && (<>
								<div className="scrollBox mt-2 p-3 mb-5 no-export"
									style={{ height: 'auto', overflowY: 'hidden', background: '#f3f6f9', position: "relative", border: "1px solid #ccc", padding: "20px" }}>
									<DashboardAdvisory
										setActiveAdvisory={setActiveAdvisory}
										countryId={selectedCountryId}
										speciesObj={speciesOptions.find(b => b.species_id === selectedSpeciesId)}
										speciesImgCode={speciesImgPath[selectedSpeciesId]}
										activeAdvisory={activeAdvisory}
										getAuthHeaders={getAuthHeaders}
										handleApiError={handleApiError}
									/>
								</div>
							</>)}
							{activeWeeklyAdvisory && (<>
								<div className="scrollBox mt-2 p-3 mb-5 no-export"
									style={{ height: 'auto', overflowY: 'hidden', background: '#f3f6f9', position: "relative", border: "1px solid #ccc", padding: "20px" }}>
									<DashboardAdvisory
										setActiveAdvisory={setActiveAdvisory}
										setActiveWeeklyAdvisory={setActiveWeeklyAdvisory}
										countryId={selectedCountryId}
										speciesObj={speciesOptions.find(b => b.species_id === selectedSpeciesId)}
										speciesImgCode={speciesImgPath[selectedSpeciesId]}
										activeAdvisory={activeWeeklyAdvisory}
										getAuthHeaders={getAuthHeaders}
										handleApiError={handleApiError}
										weekly={true}
									/>
								</div>
							</>)}
						</div>
					</div>
				</>}
			</>}
		</div>
	</div>
		<div className="pb-5"></div>
		{isLoading && (
			<div
				className="modal fade show"
				style={{
					display: "block",
					backgroundColor: "rgba(0,0,0,0.75)",
					pointerEvents: "auto",
					zIndex: 2000,
				}}
				tabIndex="-1"
				role="dialog"
			>
				<div className="modal-dialog modal-dialog-centered"
					role="document"
					style={{
						backgroundColor: "transparent",
						justifyContent: "center",
						display: "flex",
					}}
				>
					<div className="text-center p-4"
						style={{
							color: "white",
							fontSize: "1.2rem",
							backgroundColor: "transparent",
						}}
					>
						<div className="spinner-border text-light mb-3" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
					</div>
				</div>
			</div>
		)}
		<Footer />

	</>);
}

export default Dashboard;